import { addDays, parseISO } from 'date-fns';

import { zonedWallTimeToUtc } from '@/lib/timezone';

interface ClosureLike {
  title: string;
  startDate: string | Date;
  endDate: string | Date;
  allDay: boolean;
  rrule: string | null;
  practitionerIds: string[];
}

/**
 * Which of these days a closure covers, keyed by yyyy-MM-dd with its reason.
 *
 * The rule the availability check uses: a whole-day block over the whole team,
 * not repeating, spanning the day in the BUSINESS timezone. A block for one
 * person is that person's time off, not a closed clinic. The rota underneath
 * stays as it is, which is what brings it back when the closure ends.
 */
export function closedDays(
  blocks: ClosureLike[],
  days: string[],
  timeZone: string
): Map<string, string> {
  const closures = blocks.filter(
    (block) =>
      block.allDay && !block.rrule && block.practitionerIds.length === 0
  );
  const closed = new Map<string, string>();
  for (const day of days) {
    const date = parseISO(day);
    const dayStart = zonedWallTimeToUtc(date, 0, 0, timeZone);
    const dayEnd = zonedWallTimeToUtc(addDays(date, 1), 0, 0, timeZone);
    const closure = closures.find(
      (block) =>
        new Date(block.startDate) <= dayStart &&
        new Date(block.endDate) >= dayEnd
    );
    if (closure) closed.set(day, closure.title);
  }
  return closed;
}

/** "Closed", or "Closed · Staff training" when the reason says something more. */
export function closedLabel(reason: string): string {
  return reason.trim().toLowerCase() === 'closed'
    ? 'Closed'
    : `Closed · ${reason}`;
}
